import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { todayIso, STATUS_META, TONE_CLASSES } from "@/lib/entries";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PhoneCall, MessageCircle, User } from "lucide-react";

const emptyForm = (defaultStatus = "") => ({
  patient_name: "",
  entry_date: todayIso(),
  status: defaultStatus,
  notes: "",
  scheduled_date: "",
  done_date: "",
  next_date: "",
  done_method: "call",
  done_outcome: "appointment_booked",
  done_note: "",
});

function formFromEntry(e) {
  return {
    patient_name: e.patient_name || "",
    entry_date: e.entry_date || todayIso(),
    status: e.status || "next_appointment",
    notes: e.notes || "",
    scheduled_date: e.scheduled_date || e.followup_date || "",
    done_date: e.done_date || "",
    next_date: e.next_date || "",
    done_method: e.done_method || "call",
    done_outcome: e.done_outcome || "appointment_booked",
    done_note: e.done_note || "",
  };
}

function PatientNameAutocomplete({ value, onChange, required = true }) {
  const [suggestions, setSuggestions] = useState([]);
  const [openList, setOpenList] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);
  const suppressRef = useRef(false);

  useEffect(() => {
    if (suppressRef.current) { suppressRef.current = false; return; }
    if (!value.trim()) { setSuggestions([]); setOpenList(false); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/patients/suggest", { params: { q: value } });
        const list = (data.names || []).filter((n) => n.toLowerCase() !== value.trim().toLowerCase());
        setSuggestions(list);
        setActive(0);
        setOpenList(list.length > 0);
      } catch (err) { console.error("Copy to clipboard failed:", err); }
    }, 180);
    return () => clearTimeout(t);
  }, [value]);

  useEffect(() => {
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpenList(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const pick = (name) => { suppressRef.current = true; onChange(name); setOpenList(false); };
  const onKey = (e) => {
    if (!openList || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && suggestions[active]) { e.preventDefault(); pick(suggestions[active]); }
    else if (e.key === "Escape") { setOpenList(false); }
  };

  return (
    <div ref={boxRef} className="relative">
      <Input required={required} value={value}
             onChange={(e) => onChange(e.target.value)}
             onFocus={() => suggestions.length && setOpenList(true)}
             onKeyDown={onKey}
             placeholder="e.g., Rahul Sharma"
             autoComplete="off"
             data-testid="entry-name-input" />
      {openList && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-md"
             data-testid="entry-name-suggestions">
          {suggestions.map((n, i) => (
            <button key={n} type="button"
                    onMouseDown={(e) => { e.preventDefault(); pick(n); }}
                    onMouseEnter={() => setActive(i)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${i === active ? "bg-sky-50 text-sky-700" : "text-slate-700 hover:bg-slate-50"}`}
                    data-testid={`entry-name-suggestion-${i}`}>
              <User size={14} className="text-slate-400" /> {n}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


export default function EntryDialog({ open, onOpenChange, entry, onSaved, showCallLog = false, defaultStatus, hideEntryDate = false, entrySource }) {
  const isEdit = Boolean(entry?.id);
  const [form, setForm] = useState(emptyForm(defaultStatus));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(isEdit ? formFromEntry(entry) : emptyForm(defaultStatus));
  }, [open, entry, isEdit, defaultStatus]);

  const isFollowup = form.status === "ongoing_treatment" || form.status === "cancelled_no_show" || form.status === "followup_pending_other";
  const hasStatus = Boolean(form.status);
  const isBlankNew = !isEdit && !hasStatus;
  const showNotes = isEdit || (hasStatus && form.status !== "next_appointment");
  const showDoneFields = isEdit && isFollowup && showCallLog;
  const showNextField = showDoneFields && (form.done_outcome === "reschedule" || form.done_date);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.patient_name.trim()) {
      toast.error("Please enter the patient's name"); return;
    }
    if (isFollowup && !form.scheduled_date) {
      toast.error("Please set the scheduled follow-up date"); return;
    }
    if (showDoneFields && form.done_date && form.done_outcome === "reschedule" && !form.next_date) {
      toast.error("Please set the next follow-up date for the reschedule"); return;
    }
    setSaving(true);
    try {
      const effectiveStatus = form.status || "next_appointment";
      const payload = {
        patient_name: form.patient_name,
        entry_date: form.entry_date || todayIso(),
        status: effectiveStatus,
        notes: showNotes ? form.notes : "",
      };
      if (isFollowup) {
        payload.scheduled_date = form.scheduled_date;
        payload.followup_date = form.scheduled_date; // legacy mirror
        if (isEdit) {
          payload.done_date = form.done_date || null;
          payload.next_date = form.done_date ? (form.done_outcome === "reschedule" ? (form.next_date || null) : null) : null;
          payload.done_method = form.done_date ? form.done_method : null;
          payload.done_outcome = form.done_date ? form.done_outcome : null;
          payload.done_note = form.done_date ? form.done_note : "";
        }
      } else {
        payload.followup_date = null;
        payload.scheduled_date = null;
      }
      if (isEdit) {
        await api.put(`/entries/${entry.id}`, payload);
        toast.success("Entry updated");
      } else {
        if (entrySource) payload.source = entrySource;
        // No status picked → mark as "New" so Daily Log shows the Complete Entry button.
        payload.is_new = isBlankNew;
        await api.post("/entries", payload);
        toast.success(isBlankNew ? "Saved as new — complete it anytime" : "Entry saved");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="entry-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isEdit ? "Edit entry" : "New daily log entry"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update every field including logged call details. Changing status resets workflow state."
              : "Save without picking a status to mark it as New — complete it later from the Daily Log. Pick a status to fill everything in one go."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className={hideEntryDate ? "" : "grid grid-cols-2 gap-3"}>
            <div className="space-y-2">
              <Label>Patient name</Label>
              <PatientNameAutocomplete
                value={form.patient_name}
                onChange={(v) => setForm((f) => ({ ...f, patient_name: v }))}
              />
            </div>
            {!hideEntryDate && (
              <div className="space-y-2">
                <Label>Date <span className="text-xs text-slate-400">(optional)</span></Label>
                <Input type="date" value={form.entry_date}
                       onChange={set("entry_date")} data-testid="entry-date-input" />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={form.status || undefined} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger data-testid="entry-status-select">
                <SelectValue placeholder="Select a status…" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_META).map(([k, m]) => (
                  <SelectItem key={k} value={k} data-testid={`status-${k}`}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasStatus ? (
              <div className="text-xs text-slate-500">
                Goes to: <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] ${TONE_CLASSES[STATUS_META[form.status].tone]}`}>{STATUS_META[form.status].sheet}</span>
              </div>
            ) : !isEdit ? (
              <div className="text-xs text-slate-500">
                Leave blank to save as <span className="inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] bg-sky-50 text-sky-700 border-sky-100">New</span> and complete later.
              </div>
            ) : null}
          </div>

          {isFollowup && (
            <div className="space-y-2">
              <Label>Follow-up scheduled date</Label>
              <Input required type="date" value={form.scheduled_date}
                     onChange={set("scheduled_date")} data-testid="entry-scheduled-input" />
            </div>
          )}

          {showNotes && (
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea rows={3} value={form.notes} onChange={set("notes")}
                        placeholder="Anything worth remembering next time..."
                        data-testid="entry-notes-input" />
            </div>
          )}

          {showDoneFields && (
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-4"
                 data-testid="entry-call-section">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-slate-900">Log call details</div>
                  <div className="text-xs text-slate-500">Fill only if the follow-up was already done. Clear the done-date to reopen this row.</div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Follow-up done date</Label>
                <Input type="date" value={form.done_date}
                       onChange={set("done_date")} data-testid="entry-done-date-input" />
              </div>

              {form.done_date && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Communication method</Label>
                      <Select value={form.done_method} onValueChange={(v) => setForm((f) => ({ ...f, done_method: v }))}>
                        <SelectTrigger data-testid="entry-done-method-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="call"><span className="inline-flex items-center gap-2"><PhoneCall size={14} /> Call</span></SelectItem>
                          <SelectItem value="whatsapp"><span className="inline-flex items-center gap-2"><MessageCircle size={14} /> WhatsApp</span></SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Outcome</Label>
                      <Select value={form.done_outcome} onValueChange={(v) => setForm((f) => ({ ...f, done_outcome: v }))}>
                        <SelectTrigger data-testid="entry-done-outcome-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="appointment_booked">Appointment booked</SelectItem>
                          <SelectItem value="reschedule">Rescheduled</SelectItem>
                          <SelectItem value="no_further">No further follow-up needed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {form.done_outcome === "reschedule" && (
                    <div className="space-y-2">
                      <Label>Next follow-up date</Label>
                      <Input type="date" value={form.next_date}
                             onChange={set("next_date")} data-testid="entry-next-date-input" />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Call note</Label>
                    <Textarea rows={2} value={form.done_note}
                              onChange={set("done_note")}
                              placeholder="What did they say?"
                              data-testid="entry-done-note-input" />
                  </div>
                </>
              )}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                    data-testid="entry-cancel-button">Cancel</Button>
            <Button type="submit" disabled={saving}
                    className="bg-sky-500 hover:bg-sky-600 text-white"
                    data-testid="entry-save-button">
              {saving ? "Saving..." : isEdit ? "Save changes" : "Save entry"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
